import { lazy } from 'react';
import type { ComponentType } from 'react';

/** A lazy page that can be fetched ahead of its route, and is then drawn without suspending at all. */
export type PreloadableComponent<P extends object> = ComponentType<P> & {
  /** Starts fetching the page's chunk; safe to call any number of times. A failed fetch is retried on the next call. */
  preload: () => void;
};

/**
 * `React.lazy`, plus `preload()` (2026-10-28, the Supplier list's View / Edit / Duplicate).
 *
 * A plain lazy page suspends on its first render even when its chunk is already in the browser's cache, so the route's
 * `<Suspense>` flashes its full-page spinner on every first visit. Once `preload()` has resolved, this renders the
 * loaded component directly and never suspends; before that it falls back to the ordinary lazy behaviour.
 */
export function lazyWithPreload<P extends object>(
  factory: () => Promise<{ default: ComponentType<P> }>,
): PreloadableComponent<P> {
  let loaded: ComponentType<P> | undefined;
  let pending: Promise<{ default: ComponentType<P> }> | undefined;

  const load = () => {
    pending ??= factory().then(
      (module) => {
        loaded = module.default;
        return module;
      },
      (error: unknown) => {
        pending = undefined;
        throw error;
      },
    );
    return pending;
  };

  const Lazy = lazy(load);

  function Preloadable(props: P) {
    const Component = (loaded ?? Lazy) as ComponentType<P>;
    return <Component {...props} />;
  }

  return Object.assign(Preloadable, {
    preload: () => {
      load().catch(() => undefined);
    },
  });
}
